import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { Box, Typography, IconButton, Alert } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';

// Toast Context
const ToastContext = createContext();

// Toast Provider Component
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  // Add a new toast
  const showToast = useCallback((message, type = 'info', duration = 4000) => {
    const id = Date.now() + Math.random();
    const toast = {
      id,
      message,
      type,
      duration,
      createdAt: Date.now()
    };

    setToasts(prev => [...prev, toast]);

    // Auto remove after duration
    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  }, []);

  // Remove a specific toast
  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  // Clear all toasts
  const clearAll = useCallback(() => {
    setToasts([]);
  }, []);

  const contextValue = {
    toasts,
    showToast,
    removeToast,
    clearAll
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastContainer />
    </ToastContext.Provider>
  );
};

// Toast Container Component
const ToastContainer = () => {
  const { toasts, removeToast } = useContext(ToastContext);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: { xs: 10, sm: 20 },
        right: { xs: 10, sm: 20 },
        left: { xs: 10, sm: 'auto' },
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        maxWidth: { xs: 'calc(100vw - 20px)', sm: 400 },
        width: '100%',
        maxHeight: { xs: '50vh', sm: '60vh' },
        overflowY: 'auto',
        backgroundColor: 'transparent',
        '& > *': {
          transition: 'all 0.3s ease-in-out',
        },
        // Hide scrollbar
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        msOverflowStyle: 'none',
      }}
    >
      {toasts.map((toast, index) => (
        <ToastItem 
          key={toast.id}
          toast={toast} 
          onClose={() => removeToast(toast.id)}
          index={index}
        />
      ))}
    </Box>
  );
};

// Individual Toast Component
const ToastItem = ({ toast, onClose, index }) => {
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (toast.duration > 0) {
      const timer = setTimeout(() => {
        startExitAnimation();
      }, toast.duration);

      return () => clearTimeout(timer);
    }
  }, [toast.duration]);

  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return <CheckCircleIcon sx={{ color: '#fff', fontSize: { xs: 18, sm: 20 } }} />;
      case 'error':
        return <ErrorIcon sx={{ color: '#fff', fontSize: { xs: 18, sm: 20 } }} />;
      case 'warning':
        return <WarningIcon sx={{ color: '#fff', fontSize: { xs: 18, sm: 20 } }} />;
      case 'info':
      default:
        return <InfoIcon sx={{ color: '#fff', fontSize: { xs: 18, sm: 20 } }} />;
    }
  };

  const getBackgroundColor = () => {
    switch (toast.type) {
      case 'success':
        return 'rgba(76, 175, 80, 0.95)';
      case 'error':
        return 'rgba(244, 67, 54, 0.95)';
      case 'warning':
        return 'rgba(255, 152, 0, 0.95)';
      case 'info':
      default:
        return 'rgba(33, 150, 243, 0.95)';
    }
  };

  const startExitAnimation = () => {
    setIsExiting(true);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const handleClose = () => {
    startExitAnimation();
  };

  return (
    <Box
      sx={{
        transform: isExiting ? 'translateX(100%)' : 'translateX(0)',
        opacity: isExiting ? 0 : 1,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        animation: 'slideIn 0.3s ease-out forwards',
        '@keyframes slideIn': {
          '0%': {
            transform: 'translateX(100%)',
            opacity: 0,
          },
          '100%': {
            transform: 'translateX(0)',
            opacity: 1,
          }
        }
      }}
    >
      <Alert
        icon={getIcon()}
        severity={toast.type}
        onClose={handleClose}
        sx={{
          backgroundColor: getBackgroundColor(),
          color: 'white',
          borderRadius: { xs: 1, sm: 2 },
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.2)',
          transform: isExiting ? 'scale(0.98)' : 'scale(1)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          padding: { xs: '4px 8px', sm: '6px 16px' },
          minHeight: { xs: 48, sm: 56 },
          '& .MuiAlert-message': {
            padding: { xs: '4px 0', sm: '8px 0' },
            width: '100%'
          },
          '&:hover': {
            transform: isExiting ? 'scale(0.98)' : 'scale(1.01)',
            boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
          },
        }}
        action={
          <IconButton
            aria-label="close"
            color="inherit"
            size="small"
            onClick={handleClose}
            sx={{
              padding: { xs: 0.5, sm: 1 },
              transition: 'all 0.2s',
              '&:hover': {
                transform: 'rotate(90deg)',
                backgroundColor: 'rgba(255,255,255,0.1)',
              }
            }}
          >
            <CloseIcon fontSize="inherit" sx={{ fontSize: { xs: 16, sm: 18 } }} />
          </IconButton>
        }
      >
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: 500,
            fontSize: { xs: 12, sm: 14 },
            lineHeight: { xs: 2.2, sm: 1.5 },
            wordBreak: 'break-word',
            overflowWrap: 'break-word'
          }}
        >
          {toast.message}
        </Typography>
      </Alert>
    </Box>
  );
};

// Hook for using toasts
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// Custom hook that provides toast actions
export const useToastActions = (defaultDuration = 4000) => {
  const { showToast } = useToast();
  
  return {
    success: (message, duration = defaultDuration) => showToast(message, 'success', duration),
    error: (message, duration = defaultDuration) => showToast(message, 'error', duration),
    warning: (message, duration = defaultDuration) => showToast(message, 'warning', duration),
    info: (message, duration = defaultDuration) => showToast(message, 'info', duration)
  };
};

export default ToastProvider;